import { isDefined } from "@clipboard-health/util-ts";
import { Box, type SxProps, type Theme } from "@mui/material";
import { useTu } from "@src/appV2/i18n/useTu";
import { mergeSxProps } from "@src/appV2/lib";

import { Text } from "../../components/Text";
import { getSystemEventDisplay } from "../utils/customMessageDisplay";
import { formatWorkplaceTimestamp } from "../utils/formatMessageTime";

export interface ChatSystemMessageProps {
  /**
   * The Sendbird custom type string
   */
  customType: string;
  /**
   * The raw message content (may be JSON with facility/agent text)
   */
  message: string;
  /**
   * Message creation time in ms. Used only for the admin-only "WPL Time" label;
   * system events otherwise render no timestamp.
   */
  createdAt?: number;
  /**
   * IANA time zone of the workplace, used for the admin-only "WPL Time" label.
   */
  facilityTimeZone?: string;
  /**
   * When true (Clipboard admin viewer), append a workplace-local "WPL Time: …"
   * line beneath the event text. Workplace customers leave this false.
   */
  isAdmin?: boolean;
  /**
   * Optional additional styles
   */
  sx?: SxProps<Theme>;
}

/**
 * Renders system event messages (shift bookings, check-ins, cancellations, etc.)
 * as centered, muted inline text. For Clipboard admins, appends a labeled
 * workplace-local timestamp so support can tell it apart from their device time.
 */
export function ChatSystemMessage(props: ChatSystemMessageProps) {
  const { customType, message, createdAt, facilityTimeZone, isAdmin = false, sx } = props;
  const { tu } = useTu("chat");
  const { displayText } = getSystemEventDisplay(customType, message);
  const workplaceTimestamp =
    isAdmin && isDefined(createdAt)
      ? formatWorkplaceTimestamp(createdAt, facilityTimeZone)
      : undefined;

  return (
    <Box
      sx={mergeSxProps(
        {
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          paddingY: 2,
          paddingX: 4,
        },
        sx
      )}
    >
      <Text
        sx={(theme) => ({ color: theme.palette.base?.muted, textAlign: "center" })}
        variant="caption"
      >
        {displayText}
      </Text>
      {isDefined(workplaceTimestamp) && (
        <Text
          sx={(theme) => ({ color: theme.palette.base?.inactive, textAlign: "center" })}
          variant="overline"
        >
          {tu("WPL Time:")} {workplaceTimestamp}
        </Text>
      )}
    </Box>
  );
}
